import { googleAuthService } from './googleAuth.service';
import { authApi } from '@/api/auth.api';
import { useAuthStore } from '@/store/auth.store';
import { cancelAllPendingRequests } from '@/lib/axios';
import { queryClient } from '@/lib/queryClient';
import { setItem, removeItem, getItem } from '@/utils/storage.util';
import {
  TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  USER_KEY,
  V2_USER_KEY,
  USER_PROFILE_KEY,
  USER_AUTHORITY_KEY,
} from '@/config/constants';
import { hasAnyTalentChekRole } from '@/utils/roles.util';
import type {
  LoginPayload,
  RegisterPayload,
  ForgotPasswordPayload,
  ResetPasswordPayload,
  V2LoginPayload,
  V2LoginResponse,
} from '@/types/auth.types';

export const authService = {
  /**
   * V2 Login – calls POST /v2/users/login
   * Stores the full V2LoginResponse in localStorage & zustand store.
   * After login, fetches user profile and authority.
   */
  async v2Login(payload: V2LoginPayload): Promise<V2LoginResponse> {
    const { data } = await authApi.v2Login(payload);

    // Handle nested response format if server returns { message, data: { ... } }
    const responseData = (data as any).data || data;
    
    // The server uses camelCase instead of snake_case for token fields
    const accessToken = responseData.accessToken || responseData.access_token;
    const refreshToken = responseData.refreshToken || responseData.refresh_token;

    // Persist only tokens for now so the interceptor can use them for fetchAuthority
    setItem(TOKEN_KEY, accessToken);
    setItem(REFRESH_TOKEN_KEY, refreshToken);

    // Fetch user profile and authority after login
    await Promise.allSettled([
      this.fetchUserProfile(),
      this.fetchAuthority(),
    ]);

    // Role check BEFORE setting V2_USER_KEY and hydrating the store (which sets isAuthenticated to true)
    const authority = useAuthStore.getState().authority;
    const roles = authority?.roles ?? [];

    if (!hasAnyTalentChekRole(roles)) {
      await this.logout();
      throw new Error("You do not have the required role to access this application. Please contact your administrator.");
    }

    // Now it's safe to persist user data and hydrate the store, which will trigger the PublicRoute redirect
    setItem(V2_USER_KEY, responseData);
    useAuthStore.getState().setV2Auth(responseData);

    return responseData;
  },

  async login(payload: LoginPayload) {
    const { data } = await authApi.login(payload);
    const { user, tokens } = data.data;
    setItem(TOKEN_KEY, tokens.accessToken);
    setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    setItem(USER_KEY, user);
    useAuthStore.getState().setAuth(user, tokens);
    return data.data;
  },

  async register(payload: RegisterPayload) {
    const { data } = await authApi.register(payload);
    const { user, tokens } = data.data;
    setItem(TOKEN_KEY, tokens.accessToken);
    setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    setItem(USER_KEY, user);
    useAuthStore.getState().setAuth(user, tokens);
    return data.data;
  },

  async logout() {
    if (useAuthStore.getState().authenticatedEmployee) {
      return googleAuthService.logout();
    }
    // 1. Cancel in-flight API requests before the session is torn down.
    cancelAllPendingRequests();
    // 2. Revoke the session server-side (all devices).
    try {
      const accessToken = getItem<string>(TOKEN_KEY);
      const refreshToken = getItem<string>(REFRESH_TOKEN_KEY);
      
      if (accessToken && refreshToken) {
        await authApi.logout({
          access_token: accessToken,
          refresh_token: refreshToken,
          all: true,
        });
      } else {
        // Fallback for types if they were somehow not found in storage.
        await authApi.logout({
          access_token: "",
          refresh_token: "",
          all: true,
        });
      }
    } catch {
      // Silently ignore API errors – always clear local state
    }
    // 3. Remove persisted tokens and session data.
    removeItem(TOKEN_KEY);
    removeItem(REFRESH_TOKEN_KEY);
    removeItem(USER_KEY);
    removeItem(V2_USER_KEY);
    removeItem(USER_PROFILE_KEY);
    removeItem(USER_AUTHORITY_KEY);
    // 4. Drop all cached query data.
    queryClient.clear();
    // 5. Reset the auth store.
    useAuthStore.getState().clearAuth();
    // 6. Confirm completion.
    console.info('Session sign-out completed', { signedOut: !getItem<string>(TOKEN_KEY) });
  },

  async forgotPassword(payload: ForgotPasswordPayload) {
    const { data } = await authApi.forgotPassword(payload);
    return data;
  },

  async resetPassword(payload: ResetPasswordPayload) {
    const { data } = await authApi.resetPassword(payload);
    return data;
  },

  async fetchCurrentUser() {
    const { data } = await authApi.getMe();
    useAuthStore.getState().setUser(data.data);
    return data.data;
  },

  /**
   * Fetches user profile from /v2/users/me and stores in zustand + localStorage.
   */
  async fetchUserProfile() {
    const { data } = await authApi.getUserProfile();
    const profile = data.data;
    setItem(USER_PROFILE_KEY, profile);
    useAuthStore.getState().setUserProfile(profile);
    return profile;
  },

  /**
   * Fetches user authority (roles & permissions) from /v2/users/authority
   * and stores in zustand + localStorage.
   */
  async fetchAuthority() {
    const { data } = await authApi.getAuthority();
    const authority = data.data;
    setItem(USER_AUTHORITY_KEY, authority);
    useAuthStore.getState().setAuthority(authority);
    return authority;
  },
};
